// Defaults + deep validation for the crosshair config/store.
// Everything that comes from localStorage or a share code goes through here.

import {
  MAX_FX,
  MAX_LAYERS,
  MAX_SLOTS,
  clamp,
  isHex,
  newId,
  type CrosshairConfig,
  type CrosshairSlot,
  type CrosshairStore,
  type Easing,
  type Fx,
  type Layer,
  type Retrigger,
  type ShapeId,
  type Trigger,
  EASINGS,
  RETRIGGERS,
  TRIGGERS,
} from "./types";
import { SHAPE_DEFS, normalizeParams } from "./shapes";

export function makeLayer(shape: ShapeId = "cross"): Layer {
  return {
    id: newId("l"),
    shape,
    visible: true,
    color: "#ffffff",
    opacity: 1,
    blend: "normal",
    offset: { x: 0, y: 0 },
    rotation: 0,
    scale: 1,
    outline: { color: "#000000", thickness: 1, mode: "scale" },
    params: normalizeParams(shape, undefined),
    fx: [],
  };
}

/**
 * The shipped default: a white 8×3 px cross with round caps, a 1 px black
 * scale outline and a short red hit pulse. (Share code
 * `wbx1.eyJzIjoxLCJjIjp0cnVlLCJkIjpmYWxzZSwibCI6W3sicyI6ImNyb3NzIiwidiI6dHJ1ZS…`.)
 */
export function defaultConfig(): CrosshairConfig {
  const layer = makeLayer("cross");
  layer.params = {
    ...layer.params,
    top: true,
    right: true,
    bottom: true,
    left: true,
    length: 8,
    thickness: 3,
    gap: 0,
    round: true,
  };
  layer.outline = { color: "#000000", thickness: 1, mode: "scale" };
  layer.fx = [
    {
      id: "f0",
      trigger: "hit",
      attack: 40,
      hold: 40,
      release: 160,
      easing: "out",
      retrigger: "stack",
      scale: 0.25,
      rotate: 0,
      gap: 0,
      thickness: 0,
      offsetX: 0,
      offsetY: 0,
      opacity: 0,
      color: "#ff3040",
      outlineColor: null,
    },
  ];
  return { version: 1, scale: 1, crisp: true, hideWhenDead: false, layers: [layer] };
}

export function defaultSlot(): CrosshairSlot {
  return { id: "default", name: "Default", config: defaultConfig() };
}

export function defaultStore(): CrosshairStore {
  return { version: 1, slots: [defaultSlot()], activeId: "default", defaultId: "default" };
}

export function cloneStore(s: CrosshairStore): CrosshairStore {
  return JSON.parse(JSON.stringify(s)) as CrosshairStore;
}

const asBool = (v: unknown, d: boolean): boolean => (typeof v === "boolean" ? v : d);
const asNum = (v: unknown, d: number, min: number, max: number): number =>
  typeof v === "number" && Number.isFinite(v) ? clamp(v, min, max) : d;
const asHex = (v: unknown, d: string): string => (isHex(v) ? v.toLowerCase() : d);
const asText = (v: unknown, d: string, max = 32): string =>
  typeof v === "string" ? v.replace(/[\u0000-\u001f]/g, "").trim().slice(0, max) || d : d;

function normalizeFx(raw: unknown): Fx | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const trigger = TRIGGERS.includes(o.trigger as Trigger) ? (o.trigger as Trigger) : "hit";
  const easing = EASINGS.includes(o.easing as Easing) ? (o.easing as Easing) : "out";
  const retrigger = RETRIGGERS.includes(o.retrigger as Retrigger)
    ? (o.retrigger as Retrigger)
    : "stack";
  return {
    id: asText(o.id, newId("f"), 24),
    trigger,
    attack: asNum(o.attack, 60, 0, 2000),
    hold: asNum(o.hold, 40, 0, 2000),
    release: asNum(o.release, 160, 0, 2000),
    easing,
    retrigger,
    scale: asNum(o.scale, 0, -1, 3),
    rotate: asNum(o.rotate, 0, -720, 720),
    gap: asNum(o.gap, 0, -64, 64),
    thickness: asNum(o.thickness, 0, -16, 16),
    offsetX: asNum(o.offsetX, 0, -128, 128),
    offsetY: asNum(o.offsetY, 0, -128, 128),
    opacity: asNum(o.opacity, 0, -1, 1),
    color: o.color === null || o.color === undefined ? null : asHex(o.color, "#ffffff"),
    outlineColor:
      o.outlineColor === null || o.outlineColor === undefined ? null : asHex(o.outlineColor, "#000000"),
  };
}

export function normalizeLayer(raw: unknown): Layer {
  const o = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  const shape: ShapeId =
    typeof o.shape === "string" && o.shape in SHAPE_DEFS ? (o.shape as ShapeId) : "cross";
  const outlineRaw = (o.outline && typeof o.outline === "object" ? o.outline : {}) as Record<
    string,
    unknown
  >;
  const offsetRaw = (o.offset && typeof o.offset === "object" ? o.offset : {}) as Record<
    string,
    unknown
  >;
  const fxRaw = Array.isArray(o.fx) ? o.fx : [];
  const fx = fxRaw
    .map(normalizeFx)
    .filter((f): f is Fx => f !== null)
    .slice(0, MAX_FX);
  return {
    id: asText(o.id, newId("l"), 24),
    shape,
    visible: asBool(o.visible, true),
    color: asHex(o.color, "#ffffff"),
    opacity: asNum(o.opacity, 1, 0, 1),
    blend: o.blend === "additive" ? "additive" : "normal",
    offset: {
      x: asNum(offsetRaw.x, 0, -256, 256),
      y: asNum(offsetRaw.y, 0, -256, 256),
    },
    rotation: asNum(o.rotation, 0, -360, 360),
    scale: asNum(o.scale, 1, 0.1, 4),
    outline: {
      color: asHex(outlineRaw.color, "#000000"),
      thickness: asNum(outlineRaw.thickness, 1, 0, 16),
      mode: outlineRaw.mode === "persistent" ? "persistent" : "scale",
    },
    params: normalizeParams(shape, o.params as Record<string, unknown> | undefined),
    fx,
  };
}

export function normalizeConfig(raw: unknown): CrosshairConfig {
  const o = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  const layersRaw = Array.isArray(o.layers) ? o.layers : [];
  const layers = layersRaw.slice(0, MAX_LAYERS).map(normalizeLayer);
  return {
    version: 1,
    scale: asNum(o.scale, 1, 0.25, 4),
    crisp: asBool(o.crisp, true),
    hideWhenDead: asBool(o.hideWhenDead, false),
    layers: layers.length ? layers : defaultConfig().layers,
  };
}

function normalizeSlot(raw: unknown): CrosshairSlot | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  return {
    id: asText(o.id, newId("s"), 24),
    name: asText(o.name, "Crosshair", 24),
    config: normalizeConfig(o.config),
  };
}

export function normalizeStore(raw: unknown): CrosshairStore {
  const o = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  const slotsRaw = Array.isArray(o.slots) ? o.slots : [];
  const slots: CrosshairSlot[] = [];
  const seen = new Set<string>();
  for (const s of slotsRaw.slice(0, MAX_SLOTS)) {
    const slot = normalizeSlot(s);
    if (!slot || seen.has(slot.id)) continue;
    seen.add(slot.id);
    slots.push(slot);
  }
  if (!slots.length) slots.push(defaultSlot());
  if (!slots.some((s) => s.id === "default")) slots.unshift(defaultSlot());
  const ids = new Set(slots.map((s) => s.id));
  const defaultId = typeof o.defaultId === "string" && ids.has(o.defaultId) ? o.defaultId : slots[0].id;
  const activeId = typeof o.activeId === "string" && ids.has(o.activeId) ? o.activeId : defaultId;
  return { version: 1, slots, activeId, defaultId };
}

export function getSlot(store: CrosshairStore, id: string | null | undefined): CrosshairSlot {
  return store.slots.find((s) => s.id === id) ?? store.slots.find((s) => s.id === store.defaultId) ?? store.slots[0];
}

export function configFor(store: CrosshairStore, id: string | null | undefined): CrosshairConfig {
  return getSlot(store, id).config;
}

